/**
 * MARKETS — docs/03 §3.3.
 *
 * Two properties of this group carry the cold path and the caching story.
 *
 * **`include=` is the cold-path optimisation.** `GET
 * /markets/current?symbol=BTC-USD&include=orderbook,trades,candles` embeds three
 * snapshots inline, each carrying its own `seq`, so C1 reconciliation works
 * identically to the standalone endpoints. Without it the cold path is two
 * dependent HTTP waves — you cannot ask for `/markets/{id}/orderbook` until you
 * know `{id}`. With it, one: a full RTT and three requests off first paint.
 *
 * **Immutability is the caching story.** A `SETTLED` market never changes again
 * and is served `immutable`; at 2,880 markets a day that is the difference
 * between a History screen that is free after the first visit and one that
 * churns (D4).
 *
 * Every endpoint here with a `Snapshot-of` channel is called at cold load, on
 * gap recovery and on reconnect — and **never polled**.
 */

import type { HttpClient, RequestOptions } from '../client'
import type { OkBody, Page, OpQuery } from '../types'

export function marketEndpoints(client: HttpClient) {
  return {
    /** Once at cold load. Both series with period, symbol, settlement method,
     *  tie rule and current market id. */
    listSeries(options?: RequestOptions) {
      return client.get<Page<OkBody<'listSeries'>>>('/series', options)
    },

    /** Not called by the web client — everything it returns is already in
     *  `GET /series`. Kept for deep links and programmatic clients. */
    getSeries(seriesId: string, options?: RequestOptions) {
      return client.get<OkBody<'getSeries'>>(`/series/${encodeURIComponent(seriesId)}`, options)
    },

    /** Once at cold load to fill `<SeriesRail>`. **Never polled** — the rail is
     *  maintained thereafter from `series.{series_id}` events. */
    listSeriesMarkets(
      seriesId: string,
      query?: OpQuery<'listSeriesMarkets'>,
      options?: RequestOptions,
    ) {
      return client.get<Page<OkBody<'listSeriesMarkets'>>>(
        `/series/${encodeURIComponent(seriesId)}/markets`,
        { ...options, query },
      )
    },

    /**
     * History and archive only, always bounded. With no time bounds the server
     * returns the current neighbourhood; a span over 24 hours is
     * `400 RANGE_TOO_WIDE`. An unbounded list over ~1.05M rows a year is an
     * outage with a scheduler attached (D4).
     */
    listMarkets(query?: OpQuery<'listMarkets'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listMarkets'>>>('/markets', { ...options, query })
    },

    /** The single most important cold-load call. Returns the tradable market
     *  plus the one resolving behind it and the one fixing in front — the
     *  three-market strip, in one response. */
    getCurrentMarkets(query?: OpQuery<'getCurrentMarkets'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'getCurrentMarkets'>>>('/markets/current', { ...options, query })
    },

    /** Deep link; the roll's fallback (§6.5); reconciliation after a `state_seq` gap. */
    getMarket(marketId: string, options?: RequestOptions) {
      return client.get<OkBody<'getMarket'>>(`/markets/${encodeURIComponent(marketId)}`, options)
    },

    /**
     * Gap recovery on `book.{id}`, and the deep-link cold path when `include=`
     * was not used. **Not called during a normal roll.** On a settled market
     * this returns an empty book with `final: true`, not a 404 (register §D10)
     * — which is what a deep link into a resolved market renders.
     */
    getOrderBook(marketId: string, query?: OpQuery<'getOrderBook'>, options?: RequestOptions) {
      return client.get<OkBody<'getOrderBook'>>(
        `/markets/${encodeURIComponent(marketId)}/orderbook`,
        { ...options, query },
      )
    },

    /** Cold load; gap recovery on `trades.{id}`, then with `since_ms`. */
    getMarketTrades(marketId: string, query?: OpQuery<'getMarketTrades'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'getMarketTrades'>>>(
        `/markets/${encodeURIComponent(marketId)}/trades`,
        { ...options, query },
      )
    },

    /** Chart cold load in probability mode. 60 one-second bars covers the whole
     *  47-second window with headroom; there is no scrollback inside a 1-minute
     *  market. Intervals are `1s` and `5s` only. */
    getMarketCandles(marketId: string, query: OpQuery<'getMarketCandles'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'getMarketCandles'>>>(
        `/markets/${encodeURIComponent(marketId)}/candles`,
        { ...options, query },
      )
    },

    /** The complete probability track of a finished market, in one response.
     *  `immutable` once `SETTLED`. */
    getMarketPriceHistory(
      marketId: string,
      query?: OpQuery<'getMarketPriceHistory'>,
      options?: RequestOptions,
    ) {
      return client.get<Page<OkBody<'getMarketPriceHistory'>>>(
        `/markets/${encodeURIComponent(marketId)}/price-history`,
        { ...options, query },
      )
    },

    /**
     * Two callers, and only two: the user clicking "How was this settled?", and
     * the roll's watchdog when no `RESOLVED` frame arrived by
     * `resolve_eta_ms + 4000`. That watchdog request is **the only HTTP call the
     * roll is permitted to make** (§6.5).
     */
    getMarketResolution(marketId: string, options?: RequestOptions) {
      return client.get<OkBody<'getMarketResolution'>>(
        `/markets/${encodeURIComponent(marketId)}/resolution`,
        options,
      )
    },
  }
}

export type MarketEndpoints = ReturnType<typeof marketEndpoints>
